/**
 * HELP-ARTICLE.HELP
 * Guide of the page (written in Strapi), displayed on the right side of the screen
 * With a summary on the left to move between the sections, and it opens on a section when it comes from the hint of a zone
 */
import { useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { t as translate } from "i18next"
import { faBookOpen, faClipboardList, faCog } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { selectSession } from "@/features/adm/entities/session/model/session.selector"
import HelpModal from "@/components/modal-help"
import Card from "@/components/card"
import ListItem from "@/components/list-item"
import Link from "@/components/link"
import { TooltipAtPoint } from "@/components/tooltip"
import {
  filterHelpSectionsByMode,
  getHelpGuideKeyFromHref,
  getHelpGuideTooltip,
  getHelpGuidePage,
  getHelpGuideTab
} from "@/utils/help-article.utils"
import { prepareHelpHtml } from "@/utils/help-render.utils"
import { useHelpRenderContext } from "@/utils/use-help-render-context.utils"
import { useHelpArticle } from "@/utils/use-help-article.utils"
import { SESSION_DASHBOARD_TABS } from "@/redux/_session.types"

interface OwnProps {
  helpKey: string
  section?: string //Anchor of the section to show first
  isGollac?: boolean //Display of the survey : the sections of the other display are hidden
  onClose: Function
}

export const HELP_SECTION_ID_PREFIX = "help-section-"

//First parent that scrolls (the content of the window)
function getScrollParent(element: HTMLElement | null): HTMLElement | null {
  let parent = element?.parentElement ?? null
  while (parent) {
    const overflowY = window.getComputedStyle(parent).overflowY
    if (overflowY === "auto" || overflowY === "scroll") return parent
    parent = parent.parentElement
  }
  return null
}

//Icon of the title : the one of the tab of the page, a book for the guides that are not about a page
function getTitleIcon(key: string) {
  if (key === "dashboard.actions") return faClipboardList //Tab of the action plan : not one of the tabs of the dashboard
  const tab = getHelpGuideTab(key)
  return SESSION_DASHBOARD_TABS.find((x) => x.id === tab)?.icon ?? faBookOpen
}

function HelpArticleHelp(props: OwnProps) {
  //Color of the account : accent of the summary, of the titles and of the flash of a section
  const session = useSelector(selectSession)
  const accent = session.accountColors.active

  //Article displayed (the related articles replace it inside the same window)
  const [currentKey, setCurrentKey] = useState(props.helpKey)
  const loaded = useHelpArticle(currentKey)
  const isAccountCompare = !!session.accountOptions.showAccountCompareForObserver
  const article = useMemo(
    () => filterHelpSectionsByMode(loaded, !!props.isGollac, isAccountCompare),
    [loaded, props.isGollac, isAccountCompare]
  )

  //Bodies cleaned once, then the icons [rouge] [orange] [vert] [ok] [attention] [info] become FontAwesome icons
  //The thresholds, the colors and the groups of the account replace the markers of the text ({seuil-bas}, [couleur-basse]...)
  const { values, accountColors } = useHelpRenderContext(!!props.isGollac)

  const html = useMemo(() => {
    return article
      ? {
          intro: prepareHelpHtml(article.body, values, accountColors),
          sections: article.sections.map((section) => ({ ...section, body: prepareHelpHtml(section.body, values, accountColors) }))
        }
      : null
  }, [article, values, accountColors])

  //A link to the guide of another page goes to that page (where the guide opens), the other guides open in the same window
  const navigate = useNavigate()
  const location = useLocation()
  const topRef = useRef<HTMLDivElement>(null)

  function getPageOf(key: string): string | null {
    const page = getHelpGuidePage(key)
    return page && page !== location.pathname && session.isTabDisplayed(page) ? page : null
  }

  function openGuide(key: string) {
    const page = getPageOf(key)
    if (page) {
      props.onClose()
      navigate(page, { state: { openGuide: true } })
    } else {
      setCurrentKey(key)
    }
  }

  //Gollac guide : shortcut to the settings of the filters and gaps, for the administrators only (the observers have no access to the settings)
  const adminShortcut =
    article?.key === "dashboard.rps-gollac" && session.userRole === "ADMIN" ? (
      <Link
        icon={faCog}
        onClick={() => {
          props.onClose()
          navigate("/adm/settings?tab=TAB_MODULE_SURVEY")
          setTimeout(() => document.getElementById("gollac-params")?.scrollIntoView({ behavior: "smooth" }), 300)
        }}>
        {translate("adm_filter_route")}
      </Link>
    ) : undefined

    //Tooltip of the links to another guide : the links are HTML written in Strapi, so the tooltip is placed over the link by hand
  const [linkTooltip, setLinkTooltip] = useState<{ text: string; top: number; left: number } | null>(null)

  //Mouse over or keyboard focus : the keyboard users get the tooltip too
  function overContent(e: React.SyntheticEvent) {
    const link = (e.target as HTMLElement).closest("a")
    const key = getHelpGuideKeyFromHref(link?.getAttribute("href"))
    if (!link || !key) return setLinkTooltip(null)
    const rect = link.getBoundingClientRect()
    setLinkTooltip({ text: getHelpGuideTooltip(key), top: rect.top - 8, left: rect.left + rect.width / 2 })
  }

  //A link inside the text (#guide-<key>)
  function clickContent(e: React.MouseEvent) {
    setLinkTooltip(null)
    const key = getHelpGuideKeyFromHref((e.target as HTMLElement).closest("a")?.getAttribute("href"))
    if (key) {
      e.preventDefault()
      openGuide(key)
    }
  }

  //The section asked is shown once, when the guide opens : after that the reader scrolls freely
  const isSectionShown = useRef<boolean>(false)

  //Back to the top when the guide changes (the section asked can be shown again when coming back to the first guide)
  useEffect(() => {
    if (currentKey !== props.helpKey) isSectionShown.current = false
    if (currentKey !== props.helpKey || !props.section) topRef.current?.scrollIntoView({ block: "start" })
  }, [currentKey, props.helpKey, props.section])

  //Scroll to a section and flash it once
  const [highlighted, setHighlighted] = useState<string | null>(null)
  function showSection(anchor: string) {
    const element = document.getElementById(HELP_SECTION_ID_PREFIX + anchor)
    if (!element) return false
    element.scrollIntoView({ block: "start", behavior: "smooth" })
    setHighlighted(anchor)
    setTimeout(() => setHighlighted(null), 1800)
    return true
  }

  //On opening : the section asked, only for the first guide and once it is displayed
  useEffect(() => {
    if (isSectionShown.current || !article || currentKey !== props.helpKey || !props.section) return
    if (showSection(props.section)) {
      isSectionShown.current = true
      //The images above the section load after the scroll and push it down : it stays aligned for a few seconds
      alignment.current = { anchor: props.section, until: Date.now() + 4000, isReaderScrolling: false }
    }
  }, [article, currentKey, props.helpKey, props.section])

  //Keeps the section asked at the top while the images load, unless the reader already scrolls by himself
  const alignment = useRef<{ anchor: string; until: number; isReaderScrolling: boolean } | null>(null)
  function realign() {
    const current = alignment.current
    if (!current || current.isReaderScrolling || Date.now() > current.until) return
    document.getElementById(HELP_SECTION_ID_PREFIX + current.anchor)?.scrollIntoView({ block: "start" })
  }
  function readerScrolls() {
    if (alignment.current) alignment.current.isReaderScrolling = true
  }

  //Section being read, followed while the reader scrolls (displayed in the summary)
  const [activeAnchor, setActiveAnchor] = useState<string | null>(null)
  useEffect(() => {
    if (!article || article.sections.length < 2) return
    const scroller = getScrollParent(topRef.current)
    if (!scroller) return
    const anchors = article.sections.map((section) => section.anchor)

    function update() {
      if (!scroller) return
      const limit = scroller.getBoundingClientRect().top + 40
      let current = anchors[0]
      for (const anchor of anchors) {
        const element = document.getElementById(HELP_SECTION_ID_PREFIX + anchor)
        if (element && element.getBoundingClientRect().top <= limit) current = anchor
      }
      //At the very bottom the last section is the one being read, even if it is short
      if (scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 4) current = anchors[anchors.length - 1]
      setActiveAnchor(current)
    }

    update()
    scroller.addEventListener("scroll", update, { passive: true })
    return () => scroller.removeEventListener("scroll", update)
  }, [article])

  //The summary only makes sense with several sections
  const hasSummary = !!article && article.sections.length > 1

  //The benchmark guide is only offered with the benchmark module
  function isRelatedAvailable(related: { key: string }) {
    return related.key !== "dashboard.benchmark" || !!session.modules.survey.benchmark
  }

  function renderRelated() {
    return (
      article &&
      article.related.filter(isRelatedAvailable).length > 0 && (
        <div className={hasSummary ? "help-toc-related" : "mt-6"}>
          <div className="text-sm font-bold mb-2">À lire ensuite</div>
          {article.related.filter(isRelatedAvailable).map((related) => (
            <div key={related.key} className="mb-1">
              <Link isWithoutMargin icon={getTitleIcon(related.key)} onClick={() => openGuide(related.key)}>
                {related.title}
              </Link>
            </div>
          ))}
        </div>
      )
    )
  }

  return (
    <HelpModal
      isLarge
      isFullHeight
      title={
        article ? (
          <span>
            <FontAwesomeIcon icon={getTitleIcon(article.key)} style={{ color: accent, marginRight: 12 }} />
            {article.title}
          </span>
        ) : undefined
      }
      onClose={props.onClose}
      decoration={
        hasSummary ? <img src={require("@/assets/owl.png")} alt="" className="help-owl" /> : undefined
      }
      option={
        currentKey !== props.helpKey || adminShortcut ? (
          <div className="flex items-center gap-4">
            {currentKey !== props.helpKey && (
              <Link icon={getTitleIcon(props.helpKey)} onClick={() => setCurrentKey(props.helpKey)}>
                Retour au guide
              </Link>
            )}
            {adminShortcut}
          </div>
        ) : undefined
      }>
      {article === undefined && <div className="text-sm grey-t">Chargement…</div>}

      {article === null && <div className="text-sm grey-t">Ce guide n’est pas disponible pour le moment.</div>}

      {article && html && (
        <div className="help-guide" style={{ ["--help-accent" as any]: accent }}>
          {hasSummary && (
            <div className="help-toc-wrap">
              <Card className="help-toc" isWithoutPadding>
                <div className="help-toc-title">Dans ce guide</div>
                {article.sections.map((section, i) => {
                  const isActive = activeAnchor === section.anchor
                  return (
                    <ListItem
                      key={section.anchor}
                      isSmall
                      isEditable
                      isActive={isActive}
                      className={"help-toc-listitem" + (isActive ? " help-toc-listitem-active" : "")}
                      onClick={() => showSection(section.anchor)}>
                      <div
                        className="width-10 abs"
                        style={{ backgroundColor: isActive ? accent : "transparent", height: "100%", top: 0, left: 0 }}
                      />
                      <span className="help-toc-number">{i + 1}</span>
                      <span className={isActive ? "font-bold" : ""}>{section.title}</span>
                    </ListItem>
                  )
                })}
                {renderRelated()}
              </Card>
            </div>
          )}

          <div
            className="help-guide-content"
            onClick={clickContent}
            onMouseOver={overContent}
            onFocus={overContent}
            onBlur={() => setLinkTooltip(null)}
            onMouseLeave={() => setLinkTooltip(null)}
            onLoadCapture={realign}
            onWheel={() => {
              readerScrolls()
              setLinkTooltip(null)
            }}
            onTouchMove={readerScrolls}
            onKeyDown={readerScrolls}>
            <div ref={topRef} />
            {article.summary && <p className="text-sm font-bold mb-3">{article.summary}</p>}

            {html.intro && <div className="help-article" dangerouslySetInnerHTML={{ __html: html.intro }} />}

            {html.sections.map((section) => (
              <section
                key={section.anchor}
                id={HELP_SECTION_ID_PREFIX + section.anchor}
                className={
                  "help-article help-article-section" +
                  (highlighted === section.anchor ? " help-article-highlight" : "")
                }>
                <h2>{section.title}</h2>
                <div dangerouslySetInnerHTML={{ __html: section.body }} />
              </section>
            ))}

            {!hasSummary && renderRelated()}
          </div>
        </div>
      )}
      {linkTooltip && <TooltipAtPoint text={linkTooltip.text} top={linkTooltip.top} left={linkTooltip.left} />}
    </HelpModal>
  )
}

export default HelpArticleHelp
